/**
 * Tracks whether a guided demo tour is on screen, so ambient overlays (the
 * announcement lightbox) can stand aside for its duration.
 *
 * Announcements are path-targeted, so a tour that walks the visitor across
 * pages meets a different one on each — hence a session-wide signal rather
 * than a per-step one. The tour engine lives in the persistent layout, so the
 * flag survives Inertia navigations while subscribers remount and re-read it.
 */

const EVENT = "demo-tour:activity"

let active = false

export function isDemoTourActive(): boolean {
  return active
}

export function setDemoTourActive(next: boolean): void {
  if (active === next) return
  active = next
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { active: next } }))
  }
}

/** Subscribe to activity changes. Returns an unsubscribe function. */
export function onDemoTourActivity(
  handler: (active: boolean) => void,
): () => void {
  if (typeof window === "undefined") return () => undefined
  const listener = (event: Event) => {
    handler((event as CustomEvent<{ active: boolean }>).detail.active)
  }
  window.addEventListener(EVENT, listener)
  return () => window.removeEventListener(EVENT, listener)
}

const STEP_EVENT = "demo-tour:step"

/** Announces the anchor (data-tour value) the tour just spotlighted, so a page
 *  can prepare what that step shows — e.g. pre-fill a locked demo answer. */
export function announceDemoTourStep(target: string): void {
  if (typeof window === "undefined") return
  window.dispatchEvent(new CustomEvent(STEP_EVENT, { detail: { target } }))
}

/** Subscribe to spotlighted anchors. Returns an unsubscribe function. */
export function onDemoTourStep(handler: (target: string) => void): () => void {
  if (typeof window === "undefined") return () => undefined
  const listener = (event: Event) => {
    handler((event as CustomEvent<{ target: string }>).detail.target)
  }
  window.addEventListener(STEP_EVENT, listener)
  return () => window.removeEventListener(STEP_EVENT, listener)
}
